import React from 'react';
import {
  Box,
  FormHelperText,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio
} from '@mui/material';
import { useSkills } from '../../contexts/SkillsContext';
import { getSkillLevelColor } from '../../constants/skillLevels';

const TechnicianSkillSelect = ({ value, onChange, error, helperText, teamId }) => {
  const { skills, loading } = useSkills();

  // Filtrer et trier les compétences par équipe et par ordre alphabétique
  const availableSkills = React.useMemo(() => {
    let filteredSkills;
    if (!teamId) {
      filteredSkills = skills; // Si pas d'équipe sélectionnée, afficher toutes les compétences
    } else {
      // Filtrer par équipe OU compétences sans équipe assignée
      filteredSkills = skills.filter(skill => skill.teamId === teamId || !skill.teamId);
    }
    
    // Trier par ordre alphabétique
    return filteredSkills.sort((a, b) => a.name.localeCompare(b.name));
  }, [skills, teamId]);

  // Convertir value en format compatible avec les niveaux
  const skillsWithLevels = React.useMemo(() => {
    if (!Array.isArray(value)) return [];
    
    return value.map(item => {
      if (typeof item === 'object' && item.skillId) {
        // Nouveau format avec niveaux
        return item;
      } else {
        // Ancien format simple (compatibilité)
        return { skillId: item, level: 1 };
      }
    });
  }, [value]);

  // Créer un objet pour accès rapide aux compétences sélectionnées
  const selectedSkillsMap = React.useMemo(() => {
    const map = {};
    skillsWithLevels.forEach(item => {
      map[item.skillId] = item.level;
    });
    return map;
  }, [skillsWithLevels]);

  const handleSkillToggle = (skillId, isSelected) => {
    if (isSelected) {
      // Ajouter la compétence avec niveau 1 par défaut
      const newSkills = [...skillsWithLevels, { skillId, level: 1 }];
      onChange({ target: { value: newSkills } });
    } else {
      // Retirer la compétence
      const newSkills = skillsWithLevels.filter(item => item.skillId !== skillId);
      onChange({ target: { value: newSkills } });
    }
  };

  const handleLevelChange = (skillId, level) => {
    const newSkills = skillsWithLevels.map(item => 
      item.skillId === skillId ? { ...item, level: parseInt(level) } : item
    );
    onChange({ target: { value: newSkills } });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Chargement des compétences...
        </Typography>
      </Box>
    );
  }

  if (availableSkills.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Compétences et niveaux
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {teamId ? 'Aucune compétence disponible pour cette équipe' : 'Aucune compétence disponible'}
        </Typography>
        {helperText && <FormHelperText error={Boolean(error)}>{helperText}</FormHelperText>}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Compétences et niveaux
      </Typography>
      
      {/* Affichage des compétences sélectionnées */}
      {skillsWithLevels.length > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Compétences attribuées :
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {skillsWithLevels.map((item) => {
              const skill = availableSkills.find(s => s.id === item.skillId);
              if (!skill) return null;
              
              return (
                <Box
                  key={item.skillId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: `${skill.color}20`,
                    border: `2px solid ${getSkillLevelColor(item.level)}`,
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: skill.color,
                      borderRadius: '50%',
                      mr: 1,
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                  {skill.name} - Niveau {item.level}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                Compétence
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>
                Aucun
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>
                Niveau 1
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>
                Niveau 2
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableSkills.map((skill) => {
              const isSelected = selectedSkillsMap.hasOwnProperty(skill.id);
              const currentLevel = selectedSkillsMap[skill.id] || 1;
              
              return (
                <TableRow key={skill.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: skill.color,
                          borderRadius: 1,
                          mr: 1.5,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      />
                      <Typography variant="body2">
                        {skill.name}
                      </Typography>
                      {!skill.teamId && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (Globale)
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {/* Colonne Aucun */}
                  <TableCell align="center">
                    <Radio
                      checked={!isSelected}
                      onChange={() => {
                        if (isSelected) {
                          handleSkillToggle(skill.id, false);
                        }
                      }}
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        '&.Mui-checked': {
                          color: 'text.secondary',
                        },
                      }}
                    />
                  </TableCell>
                  {/* Colonne Niveau 1 */}
                  <TableCell align="center">
                    <Radio
                      checked={isSelected && currentLevel === 1}
                      onChange={() => {
                        if (!isSelected) {
                          handleSkillToggle(skill.id, true);
                        } else {
                          handleLevelChange(skill.id, 1);
                        }
                      }}
                      size="small"
                      sx={{
                        color: getSkillLevelColor(1),
                        '&.Mui-checked': {
                          color: getSkillLevelColor(1),
                        },
                      }}
                    />
                  </TableCell>
                  {/* Colonne Niveau 2 */}
                  <TableCell align="center">
                    <Radio
                      checked={isSelected && currentLevel === 2}
                      onChange={() => {
                        if (!isSelected) {
                          // Ajouter directement avec niveau 2
                          const newSkills = [...skillsWithLevels, { skillId: skill.id, level: 2 }];
                          onChange({ target: { value: newSkills } });
                        } else {
                          handleLevelChange(skill.id, 2);
                        }
                      }}
                      size="small"
                      sx={{
                        color: getSkillLevelColor(2),
                        '&.Mui-checked': {
                          color: getSkillLevelColor(2),
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {helperText && (
        <FormHelperText error={Boolean(error)} sx={{ mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}
      {teamId && (
        <FormHelperText sx={{ mt: 1 }}>
          Affichage des compétences de l'équipe sélectionnée et des compétences globales
        </FormHelperText>
      )}
    </Box>
  );
};

export default TechnicianSkillSelect; 